import React, {
  useContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useQuery } from "@tanstack/react-query";
import Item from "../Item";
import ButtonCollection from "../ButtonCollection";
import { EditContext } from "../../../editor/context/EditContext";
import { useAddElement } from "../../../editor/context/AddElementContext";
import { apiFetch } from "../../../core/utils/api";
import { getImageStyleClasses } from "../../../core/utils/imageStyleUtils";
import { useBackdropTextColor } from "../../../core/utils/backdropContrast";
import "./heroModeEditAffordances.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getSlotDef(modeDefinition, slotName) {
  return modeDefinition?.slots?.find((s) => s.name === slotName) || null;
}

// ---------------------------------------------------------------------------
// getHeroImageStyle
// ---------------------------------------------------------------------------
//
// Resolve the CSS filter affordances (opacity, brightness, contrast, blur,
// grayscale, hue-rotate, invert, saturate, sepia) a user configured on an
// image item's ``image_style`` into the same Tailwind class string the
// standalone ``Image`` component uses (via ``useImageStyle`` →
// ``getImageStyleClasses``).
//
// Hero-mode renderers paint their media either as a CSS ``background-image``
// layer (``BackgroundMediaRenderer``) or as ``<img>`` tiles (gallery /
// mosaic / spotlight / etc.). Neither path went through ``useImageStyle``,
// so the filters the user set in the Image edit panel were silently
// dropped once the image was placed inside a hero mode. Routing every
// hero image through this helper restores parity: the grayscale / blur /
// opacity / hue-rotate / … the user picks now applies on the canvas
// exactly as it does for a free-standing image.
//
// Returns ``{ className, style }`` so callers can spread both into their
// existing element. ``image_style`` is embedded on the fully-serialized
// slot assignments (``ImageSerializer.image_style``), so the data is
// available at first paint with no extra fetch. Returns empty values when
// the item has no ``image_style`` (e.g. video slots, legacy data).

export function getHeroImageStyle(imageItem) {
  if (!imageItem || !imageItem.image_style) {
    return { className: "", style: {} };
  }
  const data = getImageStyleClasses(imageItem.image_style);
  const style =
    data?.style && typeof data.style === "object" ? { ...data.style } : {};

  // ``opacity`` is special: the Tailwind ``opacity-[x]`` class in
  // ``className`` is overridden by the inline ``opacity`` that the
  // ProgressiveImage / ProgressiveHeroImage components set for their
  // load cross-fade. Surfacing it as a numeric inline value lets those
  // components multiply it into the fade so the user's opacity actually
  // takes effect on ``<img>`` tiles. For ``background-image`` layers the
  // inline value simply applies directly.
  const rawOpacity = Number(imageItem.image_style.opacity);
  if (Number.isFinite(rawOpacity)) {
    style.opacity = Math.min(1, Math.max(0, rawOpacity / 100));
  }

  return {
    className: data?.className || "",
    style,
  };
}

const SLOT_TYPE_LABELS = {
  image: "Image",
  video: "Video",
  title: "Title",
  text: "Text",
  button: "Button",
  button_collection: "Buttons",
};

function slotLabel(slotDef) {
  if (!slotDef) return "Item";
  const first = slotDef.accepts?.[0];
  return SLOT_TYPE_LABELS[first] || slotDef.label || "Item";
}

const hasFullData = (child) => {
  if (!child) return false;
  const minimalFields = ["id", "type", "order"];
  return Object.keys(child).some((f) => !minimalFields.includes(f));
};

// ---------------------------------------------------------------------------
// BackgroundMediaRenderer
// ---------------------------------------------------------------------------
//
// ``ambientClass`` is the CSS class to apply to the image layer for
// continuous motion (e.g. ``hero-ambient-ken-burns``). Videos are already
// "moving" so we don't animate them further. Callers typically compute
// this with ``useHeroAmbient(heroSlots?.ambientAnimation)``
// and pass the result straight through.

export const BackgroundMediaRenderer = React.memo(function BackgroundMediaRenderer({
  slotData,
  heroBase,
  ambientClass = "",
  heroItem,
}) {
  const isMinimalRef = slotData && !hasFullData(slotData);

  // ``apiFetch`` is a thin ``fetch`` wrapper that returns a Response; we
  // have to await it and parse JSON. Without this step ``fetchedItem``
  // would be the raw Response and ``image_url`` would always be
  // undefined — the bug that makes Split's media slot look empty when
  // the slot assignment is a minimal {id,type,order} ref.
  const { data: fetchedItem } = useQuery({
    queryKey: ["item", slotData?.type, slotData?.id],
    queryFn: async () => {
      const res = await apiFetch(
        `/api/items/${slotData.type}/${slotData.id}/`,
      );
      if (!res.ok) {
        throw new Error(
          `Failed to fetch ${slotData.type} ${slotData.id}: ${res.status}`,
        );
      }
      return res.json();
    },
    enabled: !!isMinimalRef && !!slotData?.id && !!slotData?.type,
    staleTime: 30_000,
  });

  const resolved = isMinimalRef && fetchedItem ? fetchedItem : slotData;
  // Images may have a crop applied — prefer the cropped URL when present
  // so the media slot shows exactly what the user edited.
  const resolvedImageUrl =
    resolved?.cropped_image_url ||
    resolved?.image_url ||
    resolved?.background_image_url;
  const resolvedVideoUrl = resolved?.video_url;
  const resolvedIsVideo = resolved?.type === "video" || !!resolvedVideoUrl;

  if (resolvedIsVideo && resolvedVideoUrl) {
    return (
      <>
        <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
          <video
            src={resolvedVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            style={{ pointerEvents: "none" }}
          />
        </div>
        <EditableSlotMediaOverlay slotData={resolved} heroItem={heroItem} />
      </>
    );
  }

  if (resolvedImageUrl) {
    // Honour the user's image filters (grayscale / blur / opacity /
    // hue-rotate / …). These come from ``image_style`` on the resolved
    // image item and are applied as the same Tailwind filter classes the
    // standalone Image component uses, so a background-media hero image
    // styles identically to a free-standing one.
    const { className: filterClass, style: filterStyle } =
      getHeroImageStyle(resolved);
    return (
      <>
        <div
          className={`absolute inset-0 w-full h-full bg-cover bg-center z-0 ${ambientClass} ${filterClass}`.trim()}
          style={{ backgroundImage: `url(${resolvedImageUrl})`, ...filterStyle }}
        />
        <EditableSlotMediaOverlay slotData={resolved} heroItem={heroItem} />
      </>
    );
  }

  return null;
});

// ---------------------------------------------------------------------------
// EditableSlotMediaOverlay (edit-mode only)
// ---------------------------------------------------------------------------
//
// Transparent overlay that sits on top of media rendered by hero modes
// (background images, ``<img>`` tiles, videos). In edit mode it intercepts
// hover and click events so the user can select the underlying item
// directly on the canvas — without having to navigate to the slot
// assignment panel.
//
// Props:
//   * ``slotData``  — the resolved item object (must have ``id`` + ``type``).
//   * ``heroItem``  — the parent hero item; used to derive the
//                     ``component`` id that ``selectElement`` needs.
//   * ``className`` — extra classes merged onto the overlay (default
//                     ``"absolute inset-0"`` for background-media use;
//                     callers rendering inside a ``relative`` tile wrapper
//                     may pass their own positioning classes).

export function EditableSlotMediaOverlay({
  slotData,
  heroItem,
  className = "absolute inset-0",
}) {
  const editContext = useContext(EditContext);
  const [isHovered, setIsHovered] = useState(false);

  if (!editContext || !slotData?.id || !slotData?.type) return null;

  const { selectElement, selectedElement } = editContext;
  const isSelected =
    selectedElement?.id === slotData.id &&
    selectedElement?.type === slotData.type;

  const handleClick = (e) => {
    e.stopPropagation();
    if (!selectElement) return;
    const elementForSelect = {
      ...slotData,
      component: slotData.component || heroItem?.component,
    };
    selectElement(elementForSelect);
  };

  const typeLabel =
    SLOT_TYPE_LABELS[slotData.type] || slotData.type || "Media";

  return (
    <div
      className={`${className} z-10 hero-slot-media-overlay ${
        isSelected
          ? "hero-slot-media-overlay--selected"
          : isHovered
            ? "hero-slot-media-overlay--hovered"
            : ""
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      style={{ cursor: "pointer" }}
    >
      {(isHovered || isSelected) && (
        <span className="hero-slot-media-overlay__label">
          {typeLabel}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptySlotPlaceholder (edit-mode only)
// ---------------------------------------------------------------------------

export function EmptySlotPlaceholder({ slotDef, heroId, order, fill = false }) {
  const { openAddModal } = useAddElement();
  const label = slotLabel(slotDef);
  const firstType = slotDef?.accepts?.[0];
  const buttonRef = useRef(null);
  const { textColor, remeasure } = useBackdropTextColor(buttonRef);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      openAddModal(
        {
          heroId,
          order: order ?? 0,
          ...(firstType ? { restrictToType: firstType } : {}),
        },
        e,
      );
    },
    [openAddModal, heroId, order, firstType],
  );

  // ``fill`` swaps the default "ribbon" sizing (``w-full`` + intrinsic
  // height from ``py-4``) for "stretch to fill the parent" sizing
  // (``w-full h-full`` with no fixed vertical padding). Used by callers
  // that drop the placeholder into a fixed-size slot (e.g. a gallery
  // card next to real photos) so the dashed outline matches the
  // surrounding card silhouette instead of collapsing to a short
  // ribbon at the top of the card.
  const sizingClasses = fill ? "w-full h-full px-3" : "w-full py-4 px-3";
  const hoverRevealClass = fill
    ? "hero-edit-add-affordance hero-edit-add-affordance--fill"
    : "hero-edit-add-affordance";

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      onMouseEnter={remeasure}
      data-tone={textColor === "black" ? "black" : undefined}
      className={`${hoverRevealClass} ${sizingClasses} rounded-lg
                 flex items-center justify-center gap-2
                 cursor-pointer bg-transparent`}
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      <span className="hero-edit-add-affordance__label text-xs font-medium">
        Add {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// EmptyMediaSlotAffordance (edit-mode only)
// ---------------------------------------------------------------------------
//
// Renders the standard ``EmptySlotPlaceholder`` ("+ Add Image") for a
// mode's media/gallery slot when:
//   * the editor is open (``EditContext`` is mounted), AND
//   * the mode actually defines that slot.
//
// Used by every image-bearing mode (Split / Magazine / Stack /
// Gallery / Mosaic / Spotlight Swap / Photo Banner / Magazine Swap /
// Gallery Strip) inside its "no media yet" fallback branch so editors
// always see a one-click way to populate the image slot — matching
// the affordance Profile mode already shows for its empty
// ``portrait`` slot. In published render mode the wrapper returns
// ``null`` so visitors never see the dashed-border button.

export function EmptyMediaSlotAffordance({
  heroSlots,
  heroId,
  slotName,
  order = 0,
  className = "",
  fill = false,
}) {
  const editContext = useContext(EditContext);
  if (!editContext) return null;

  const slotDef = getSlotDef(heroSlots?.modeDefinition, slotName);
  if (!slotDef) return null;

  // When the affordance is filling a fixed-size container (e.g. a
  // gallery card slot), the wrapper itself also needs to stretch so
  // the dashed-outline button can fill it in turn — otherwise the
  // wrapper collapses to ``height: auto`` and the ``h-full`` on the
  // inner button has nothing to stretch against.
  const wrapperClass = fill
    ? `w-full h-full ${className}`.trim()
    : `hero-edit-add-wrapper ${className}`.trim();

  return (
    <div className={wrapperClass}>
      <EmptySlotPlaceholder
        slotDef={slotDef}
        heroId={heroId}
        order={order}
        fill={fill}
      />
    </div>
  );
}

export function SlotCapacityAffordance({
  heroSlots,
  heroId,
  slotName,
  currentCount,
  order,
  className = "",
  fill = false,
}) {
  const slotDef = getSlotDef(heroSlots?.modeDefinition, slotName);
  const maxCount = Number(slotDef?.max_count);
  if (!Number.isFinite(maxCount) || maxCount <= 1) return null;
  if (currentCount >= maxCount) return null;

  return (
    <EmptyMediaSlotAffordance
      heroSlots={heroSlots}
      heroId={heroId}
      slotName={slotName}
      order={order ?? currentCount}
      className={className}
      fill={fill}
    />
  );
}

// ---------------------------------------------------------------------------
// SlotRenderer
// ---------------------------------------------------------------------------

export function SlotRenderer({
  slotName,
  heroSlots,
  heroBase,
  heroId,
  slotIndex,
  className,
  style: externalStyle,
}) {
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const rawSlotData = heroSlots?.getSlot(slotName);
  // ``SlotRenderer`` is the single-item path (title, text, cta, etc.).
  // Multi-item slots (``max_count > 1`` — currently just ``gallery``) are
  // rendered by their mode's custom track/grid, not here. Arrays are
  // down-leveled to "empty" so the placeholder/stash logic still fires
  // instead of crashing on ``slotData.type``. If a mode mistakenly wires
  // a multi-item slot through ``SlotRenderer`` in the future this is
  // safe: users see the same empty-slot affordance they'd get for an
  // unassigned single slot.
  const slotData = Array.isArray(rawSlotData)
    ? (rawSlotData.length === 1 ? rawSlotData[0] : null)
    : rawSlotData;
  const slotDef = getSlotDef(heroSlots?.modeDefinition, slotName);
  const bp = heroBase?.bp || "medium";
  const fontSize = slotDef?.responsive_font_sizes?.[bp];
  const fitSingleLine = slotDef?.fit_single_line || false;

  const fitRef = useRef(null);
  const fittedSize = useFitSingleLine(fitRef, fontSize, fitSingleLine && !!slotData);

  if (!slotData) {
    if (isEditMode && slotDef) {
      const placeholderClass = `hero-edit-add-wrapper ${className || ""}`.trim();
      return (
        <div className={placeholderClass} style={externalStyle}>
          <EmptySlotPlaceholder
            slotDef={slotDef}
            heroId={heroId}
            order={slotIndex ?? 0}
          />
        </div>
      );
    }
    return null;
  }

  const isFull = hasFullData(slotData);
  const isButtonType =
    slotData.type === "button_collection" || slotData.type === "button";

  const fontStyle = fittedSize
    ? { fontSize: `${fittedSize}px` }
    : fontSize
      ? { fontSize: `${fontSize}px` }
      : undefined;

  const combinedStyle = { ...externalStyle, ...fontStyle };

  const itemEl = isButtonType ? (
    <ButtonCollection
      {...(isFull ? { item: slotData } : { itemRef: { id: slotData.id, type: slotData.type } })}
      containerWidth={heroBase?.width}
    />
  ) : (
    <Item
      {...(isFull ? { item: slotData } : { itemRef: { id: slotData.id, type: slotData.type } })}
    />
  );

  return (
    <div
      ref={fitSingleLine ? fitRef : undefined}
      className={className}
      style={combinedStyle}
    >
      {itemEl}
    </div>
  );
}

// ---------------------------------------------------------------------------
// useFitSingleLine
// ---------------------------------------------------------------------------

export function useFitSingleLine(ref, baseFontSize, enabled) {
  const [computedSize, setComputedSize] = useState(baseFontSize || null);
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    if (!enabled || !baseFontSize) {
      setComputedSize(baseFontSize || null);
      return;
    }
    setComputedSize(baseFontSize);
  }, [enabled, baseFontSize]);

  useEffect(() => {
    if (!enabled || !ref?.current || !baseFontSize) return;

    const el = ref.current;
    const minSize = Math.max(12, Math.round(baseFontSize * 0.5));

    const measure = () => {
      if (!el || !el.firstElementChild) return;
      let size = baseFontSize;
      el.style.fontSize = `${size}px`;

      while (size > minSize && el.scrollWidth > el.clientWidth + 1) {
        size -= 2;
        el.style.fontSize = `${size}px`;
      }
      setComputedSize(size);
    };

    const debouncedMeasure = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };

    debouncedMeasure();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(debouncedMeasure);
      ro.observe(el);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro?.disconnect();
    };
  }, [ref, baseFontSize, enabled]);

  return enabled ? computedSize : baseFontSize || null;
}

// ---------------------------------------------------------------------------
// useHeroAnimation
// ---------------------------------------------------------------------------

export function useHeroAnimation(isAnimated, animationName, { delay = 0 } = {}) {
  const ref = useRef(null);
  const [triggered, setTriggered] = useState(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
    );
  }, []);

  const disabled = !isAnimated || prefersReducedMotion;

  useEffect(() => {
    if (disabled || !ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [disabled]);

  if (disabled) {
    return { ref, className: "", style: {} };
  }

  const className = triggered ? `hero-anim-${animationName}` : "hero-anim-hidden";
  const style = delay > 0 ? { animationDelay: `${delay}ms` } : {};

  return { ref, className, style };
}

// ---------------------------------------------------------------------------
// useHeroAmbient
// ---------------------------------------------------------------------------
//
// Ambient animations are continuous effects (ken-burns, float, pulse,
// marquee) that a mode declares via ``modeDefinition.ambient_animation``.
// Unlike ``useHeroAnimation`` they have no entrance lifecycle — there's
// nothing to trigger, they just start as soon as the element mounts.
//
// Ownership model: ambient motion is part of a mode's *identity*, not a
// user-flippable toggle. The backend ships paired modes (e.g.
// ``centered`` and ``centered_animated``); picking the animated variant
// is how a user opts in to ongoing motion. Because of that, this hook
// doesn't consult any "is animated" flag from the hero — if the mode
// declares an ambient animation we run it, otherwise we don't.
//
// The one runtime gate we keep is ``prefers-reduced-motion``: users
// who asked the OS to suppress motion should never see continuous
// animation even on an animated mode. The CSS file already resets
// styles under that media query at the selector level; we also
// return "" here so React never attaches the class, avoiding
// composition-time surprises (e.g. ``will-change`` promotion).
//
// Returns the CSS class name to apply, or an empty string when there's
// no ambient motion (either the mode didn't declare one or the user
// prefers reduced motion).

export function useHeroAmbient(ambientName) {
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false
    );
  }, []);

  if (prefersReducedMotion) return "";
  if (!ambientName || ambientName === "none") return "";
  return `hero-ambient-${ambientName}`;
}

// ---------------------------------------------------------------------------
// useMarqueeProximityRate
// ---------------------------------------------------------------------------
//
// Visitor-driven cadence for marquee-style ambient animations. Used by
// ``gallery_proximity`` (and any future sibling that wants the same
// "the strip slows down as you approach it" interaction) on top of
// the existing CSS-driven marquee classes (``hero-ambient-marquee-y``
// / ``hero-ambient-marquee-x``).
//
// Why playbackRate (and not animation-duration):
//   * The CSS animation runs from a single ``@keyframes`` rule with
//     a fixed duration. Mutating ``animation-duration`` mid-flight
//     (or its driving CSS custom property) causes most browsers to
//     restart the animation from frame 0 — the strip would visibly
//     snap back every time the cursor moved, which is exactly the
//     opposite of "smooth cadence response". The Web Animations API
//     exposes ``Animation.playbackRate`` as a live multiplier on
//     the animation's effective duration, so setting it to ``0.4``
//     decelerates a 30s loop to read as 75s without restarting and
//     setting it back to ``1.5`` accelerates it to read as 20s,
//     again without a seam.
//   * ``playbackRate`` clamps cleanly: setting it to a small
//     non-zero value (instead of 0) keeps the marquee creeping
//     forward at the closest-approach point so the strip never
//     reads as "broken / stalled" — pausing entirely would be
//     ambiguous with "the page is frozen".
//
// Distance → rate curve:
//   * Distance is measured from the cursor to the *nearest edge* of
//     the element's bounding rect (clamped at 0 inside the rect).
//     Using the bounding rect rather than the centre means the
//     strip starts decelerating the moment the cursor enters the
//     element's neighbourhood, regardless of the element's aspect
//     ratio — a wide horizontal strip and a tall vertical column
//     both feel responsive at the same cursor offset.
//   * The mapping is linear from ``minRate`` at distance 0 to
//     ``maxRate`` at distance ``maxDistance`` (default 600px), then
//     held at ``maxRate`` for any larger distance. Linear is the
//     simplest curve that delivers the perceptual story ("closer →
//     slower"); fancier easing buys nothing here because the
//     visitor's hand is already the slow input.
//   * Below ~minDistance px (default 0) the rate stays pinned to
//     ``minRate`` so the marquee doesn't stutter when the cursor
//     wiggles across the element's edge.
//
// Reduced-motion bypass:
//   * If the visitor has ``(prefers-reduced-motion: reduce)`` set,
//     the hook becomes a no-op (no listener, no rate adjustments).
//     The underlying CSS already suppresses ambient animation under
//     the same media query (see ``heroModeAnimations.css``) so the
//     marquee is stationary anyway and there's nothing to modulate.
//   * If the user-agent doesn't support
//     ``Element.getAnimations()`` (very old browsers), the hook
//     also no-ops — the marquee still runs at its CSS-default
//     cadence, just without the visitor-reactive layer.
//
// Lifecycle:
//   * Listener is attached to ``window`` (not the element) so the
//     rate updates *as the cursor approaches from outside the
//     element*, not only after entering it. ``passive: true`` keeps
//     scroll performance unaffected.
//   * The current set of active ``Animation`` objects is sampled
//     once on mount (and re-sampled if the element identity
//     changes) — picking up animations that mount asynchronously
//     after the first frame is handled by an additional
//     ``MutationObserver`` on the element so adding the marquee
//     class later (e.g. the user toggles on motion in the editor)
//     still wires up.
//
// Args:
//   * ``ref``        — React ref to the element whose CSS animation
//                      should react to cursor distance. The element
//                      MUST already carry a CSS animation (the hook
//                      doesn't start one); typically this is the
//                      same element that has the
//                      ``hero-ambient-marquee-*`` class.
//   * ``enabled``    — gate; when ``false`` (e.g. mode is not
//                      ``gallery_proximity``) the hook is a no-op.
//   * ``minRate``    — playbackRate at distance 0. Defaults to
//                      ``0.15`` — slow enough to read as "drifting"
//                      without stalling.
//   * ``maxRate``    — playbackRate at ``maxDistance`` and beyond.
//                      Defaults to ``2.5`` — meaningfully faster
//                      than the CSS default so visitors notice the
//                      acceleration.
//   * ``maxDistance``— pixel distance at which the rate plateaus
//                      at ``maxRate``. Defaults to ``600`` — a
//                      typical viewport-scale "far away" distance.

export function useMarqueeProximityRate(
  ref,
  enabled,
  { minRate = 0.15, maxRate = 2.5, maxDistance = 600 } = {},
) {
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;

    const prefersReducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ||
      false;
    if (prefersReducedMotion) return undefined;

    const el = ref?.current;
    if (!el) return undefined;
    if (typeof el.getAnimations !== "function") return undefined;

    const baseline = Math.min(maxRate, Math.max(minRate, 1));

    const collectAnimations = () => {
      try {
        return el.getAnimations({ subtree: true }) || [];
      } catch {
        return [];
      }
    };

    const setRate = (rate) => {
      const clamped = Math.min(maxRate, Math.max(minRate, rate));
      for (const anim of collectAnimations()) {
        try {
          anim.playbackRate = clamped;
        } catch {
          // Some animations (CSS transitions, finished animations)
          // reject playbackRate writes. Skip them silently — the
          // marquee animation we care about will accept the write.
        }
      }
    };

    setRate(baseline);

    let pendingFrame = null;
    let lastEvent = null;

    const computeRateForEvent = (evt) => {
      const rect = el.getBoundingClientRect();
      const cx = evt.clientX;
      const cy = evt.clientY;
      const dx = Math.max(rect.left - cx, 0, cx - rect.right);
      const dy = Math.max(rect.top - cy, 0, cy - rect.bottom);
      const distance = Math.hypot(dx, dy);
      if (distance >= maxDistance) return maxRate;
      const t = distance / maxDistance;
      return minRate + (maxRate - minRate) * t;
    };

    const flush = () => {
      pendingFrame = null;
      if (!lastEvent) return;
      setRate(computeRateForEvent(lastEvent));
    };

    const onMove = (evt) => {
      lastEvent = evt;
      if (pendingFrame !== null) return;
      pendingFrame = window.requestAnimationFrame(flush);
    };

    window.addEventListener("mousemove", onMove, { passive: true });

    let mo = null;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => {
        if (lastEvent) {
          setRate(computeRateForEvent(lastEvent));
        } else {
          setRate(baseline);
        }
      });
      try {
        mo.observe(el, {
          attributes: true,
          attributeFilter: ["class", "style"],
          subtree: true,
        });
      } catch {}
    }

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (pendingFrame !== null) {
        try {
          window.cancelAnimationFrame(pendingFrame);
        } catch {}
      }
      try {
        mo?.disconnect();
      } catch {}
      // Restore baseline so a later mode switch doesn't inherit a
      // stale rate from this hook's last frame.
      try {
        for (const anim of collectAnimations()) {
          anim.playbackRate = 1;
        }
      } catch {}
    };
  }, [ref, enabled, minRate, maxRate, maxDistance]);
}

// ---------------------------------------------------------------------------
// useHeroVerticalPadding
// ---------------------------------------------------------------------------
//
// Read the hero root's resolved vertical padding (px) so a child can
// "break out" of it. The hero root carries the user's configured
// spacing (``p-*``/``py-*``/``pt-*``/``pb-*`` from ``useSpacing``),
// and inner content sits *inside* that padding box — which means
// any media column or photo strip that's supposed to bleed to the
// hero's outer edge would otherwise show a band of background above
// and below it equal to the hero's top / bottom padding.
//
// Hero modes whose visual identity is "image bleeds from edge to
// edge" (Split's media half, Photo Banner's photo strip, …) call
// this hook with the ``heroRootRef`` and pass the result into a
// negative-margin (or negative-inset) style on the element that
// should escape the padding.
//
// Returns ``{ top, bottom }`` in CSS pixels. Returns ``{ top: 0,
// bottom: 0 }`` until the first measurement completes (server render
// / first paint), which means the first paint shows the inset
// element — but the measured paint kicks in on the same animation
// frame, so the visible flash is imperceptible. Importantly,
// returning zeros (rather than ``null``) lets callers spread the
// result into a style object unconditionally without a flash of
// "no margin" in SSR.
//
// Re-measures on:
//   * ResizeObserver  → window resizes, breakpoint flips, font-loading
//                       reflows that change the hero's resolved padding.
//   * MutationObserver on ``class`` / ``style`` → spacing-token
//                       changes from the editor (the spacing field
//                       swaps the Tailwind class on the hero root, and
//                       the resolved padding changes without a resize).

export function useHeroVerticalPadding(heroRootRef) {
  const [pad, setPad] = useState({ top: 0, bottom: 0 });

  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = heroRootRef?.current;
    if (!el) return undefined;

    const measure = () => {
      try {
        const cs = window.getComputedStyle(el);
        const top = parseFloat(cs.paddingTop || "0") || 0;
        const bottom = parseFloat(cs.paddingBottom || "0") || 0;
        setPad((prev) => {
          if (
            Math.abs((prev.top ?? 0) - top) < 0.5 &&
            Math.abs((prev.bottom ?? 0) - bottom) < 0.5
          ) {
            return prev;
          }
          return { top, bottom };
        });
      } catch {
        // Non-fatal: keep the last measurement.
      }
    };

    measure();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      try {
        ro.observe(el);
      } catch {}
    }

    let mo = null;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(() => measure());
      try {
        mo.observe(el, { attributes: true, attributeFilter: ["class", "style"] });
      } catch {}
    }

    return () => {
      try {
        ro?.disconnect();
      } catch {}
      try {
        mo?.disconnect();
      } catch {}
    };
  }, [heroRootRef]);

  return pad;
}
